import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import FileDownload from "js-file-download";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import { financierActions } from "../../../context/actions/financierActions";
import useFinanceList from "./hooks/useFinanceList";
import EnveloppeDashboard from "./components/EnveloppeDashboard";
import TodosProgressionBadge from "./components/TodosProgressionBadge";
import financeV2Adapters from "../../../adapters/financeV2Adapters";
import financeApiService from "../../../services/financeApiService";
import tools from "../../../helpers/tools";
import Loader from "../../common/Loader";
import CustomPagination from "../../common/CustomPagination";
import styles from "./FinanceListV2.module.scss";

const STATUTS = [
  { value: "", label: "Tous les statuts" },
  { value: "1", label: "Brouillon" },
  { value: "2", label: "Demande envoyée" },
  { value: "3", label: "Accord reçu" },
  { value: "4", label: "Dossier complet" },
  { value: "5", label: "Dossier transmis" },
  { value: "6", label: "Transmis et signé" },
  // LS-629 — exclue par défaut ("Tous les statuts" n'envoie pas de filtre côté API),
  // accessible en la sélectionnant explicitement.
  { value: "7", label: "Abandonnée" },
];

// LS-717 — états du tri par compte comptable (valeur du filtre `tri`) :
// icône affichée, titre du bouton et valeur envoyée au prochain clic.
const TRI_COMPTE_INACTIF = {
  icone: "fa-sort",
  titre: "Trier par compte comptable",
  suivant: "compte_comptable",
};
const TRIS_COMPTE = {
  compte_comptable: {
    icone: "fa-sort-down",
    titre: "Tri décroissant (le plus récent en tête) : cliquer pour le croissant",
    suivant: "compte_comptable_asc",
  },
  compte_comptable_asc: {
    icone: "fa-sort-up",
    titre: "Tri croissant (le plus ancien en tête) : cliquer pour le décroissant",
    suivant: "compte_comptable",
  },
};

const FinanceListV2 = (props) => {
  const [state, dispatch] = useStore();
  const actions = financierActions(state, dispatch, props);
  const financiers = state.financier.items;
  const [isExporting, setIsExporting] = useState(false);

  const {
    filters,
    updateFilter,
    resetFilters,
    setPage,
    demandes,
    meta,
    isLoading,
  } = useFinanceList();

  // LS-717 — le clic sur l'en-tête alterne décroissant / croissant ; le
  // bouton « Réinitialiser les filtres » revient au tri par date de création.
  const triCompte = TRIS_COMPTE[filters.tri] || TRI_COMPTE_INACTIF;

  useEffect(() => {
    actions.getAllFinanciers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LS-628 — export xlsx de la liste, mêmes filtres que ceux actuellement appliqués à
  // l'écran (indépendamment de la pagination).
  async function handleExport() {
    setIsExporting(true);
    try {
      const query = tools.getSearchString(
        financeV2Adapters.toApi.filtresDemandes(filters),
      );
      const res = await financeApiService.exporterDemandes(query);
      FileDownload(res.data, "demandes-financement.xlsx");
    } catch {
      toast.error("L'export des demandes a échoué.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className={styles.financeList}>
      <div className={styles.header}>
        <h1>Demandes de financement</h1>
        <div className={styles.headerActions}>
          <button
            className={styles.btnExport}
            onClick={handleExport}
            disabled={isExporting}
          >
            <i
              className={`fa fa-${isExporting ? "spinner fa-spin" : "download"}`}
              style={{ marginRight: 6 }}
            />
            {isExporting ? "Export..." : "Exporter"}
          </button>
          <button
            className={styles.btnNew}
            onClick={() => props.history.push("/financements/create")}
          >
            <i className="fa fa-plus" style={{ marginRight: 6 }} />
            Nouvelle demande
          </button>
        </div>
      </div>

      <EnveloppeDashboard financiers={financiers} filters={filters} />

      <div className={styles.filterBar}>
        <input
          className={styles.filterInput}
          placeholder="Référence devis…"
          value={filters.devisReference}
          onChange={(e) => updateFilter("devisReference", e.target.value)}
        />
        {/* LS-716 — recherche partielle sur le n° de contrat financeur. */}
        <input
          className={styles.filterInput}
          placeholder="N° contrat financeur…"
          value={filters.numeroContrat}
          onChange={(e) => updateFilter("numeroContrat", e.target.value)}
        />
        {/* LS-717 — recherche partielle sur le compte comptable du dossier. */}
        <input
          className={styles.filterInput}
          placeholder="Compte comptable…"
          value={filters.compteComptable}
          onChange={(e) => updateFilter("compteComptable", e.target.value)}
        />
        {/* LS-718 — recherche insensible au format de saisie (ga-444 → GA444HY). */}
        <input
          className={styles.filterInput}
          placeholder="Immatriculation…"
          value={filters.immatriculation}
          onChange={(e) => updateFilter("immatriculation", e.target.value)}
        />
        <select
          className={styles.filterSelect}
          value={filters.statut}
          onChange={(e) => updateFilter("statut", e.target.value)}
        >
          {STATUTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filters.financeurUuid}
          onChange={(e) => updateFilter("financeurUuid", e.target.value)}
        >
          <option value="">Tous les financeurs</option>
          {financiers.map((f) => (
            <option key={f.uuid} value={f.uuid}>
              {f.name}
            </option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filters.typeFinancement}
          onChange={(e) => updateFilter("typeFinancement", e.target.value)}
        >
          <option value="">Tous les types</option>
          {Object.entries(financeV2Adapters.FINANCE_TYPE_MAP).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input
          type="date"
          className={styles.filterDate}
          value={filters.dateDebut}
          onChange={(e) => updateFilter("dateDebut", e.target.value)}
        />
        <input
          type="date"
          className={styles.filterDate}
          value={filters.dateFin}
          onChange={(e) => updateFilter("dateFin", e.target.value)}
        />
        <select
          className={styles.filterSelect}
          value={filters.echeancierRenseigne}
          onChange={(e) => updateFilter("echeancierRenseigne", e.target.value)}
        >
          <option value="">Échéancier (tous)</option>
          <option value="1">Échéancier OK</option>
          <option value="0">Échéancier incomplet</option>
        </select>
        <button
          className={styles.filterReset}
          onClick={resetFilters}
          title="Réinitialiser les filtres"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                {/* LS-717 — tri par compte comptable, décroissant puis
                    croissant au clic ; dossiers sans compte en dernier. */}
                <th>
                  <button
                    type="button"
                    className={styles.thSort}
                    onClick={() => updateFilter("tri", triCompte.suivant)}
                    title={triCompte.titre}
                  >
                    Compte / Référence <i className={`fa ${triCompte.icone}`} />
                  </button>
                </th>
                <th>N° contrat</th>
                <th>Financeur</th>
                <th>Devis</th>
                <th>Immatriculations</th>
                <th>Investissement</th>
                <th>Statut</th>
                <th>Statut modifié le</th>
                <th>Todos</th>
                <th>Échéancier</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {demandes.length === 0 && (
                <tr>
                  <td colSpan={11} className={styles.empty}>
                    Aucune demande trouvée.
                  </td>
                </tr>
              )}
              {demandes.map((d) => (
                <tr key={d.uuid}>
                  {/* LS-717 — le compte comptable remplace la référence
                      interne quand il est saisi ; la référence reste au survol. */}
                  <td
                    className={styles.cellRef}
                    title={
                      d.compteComptable ? `Référence : ${d.reference}` : undefined
                    }
                  >
                    {d.compteComptable || d.reference}
                  </td>
                  {/* LS-716 — n° attribué par le financeur, saisi dans le détail. */}
                  <td>{d.numeroContratFinanceur || "—"}</td>
                  <td>{d.financeur.nom}</td>
                  <td className={styles.cellDevis}>
                    {d.devisReferences.join(", ")}
                  </td>
                  {/* LS-718 — 3 premières immatriculations, le reste résumé en
                      « +n » (détail complet au survol). */}
                  <td
                    className={styles.cellDevis}
                    title={d.immatriculations.join(", ")}
                  >
                    {d.immatriculations.length === 0
                      ? "—"
                      : d.immatriculations.slice(0, 3).join(", ") +
                        (d.immatriculations.length > 3
                          ? ` +${d.immatriculations.length - 3}`
                          : "")}
                  </td>
                  <td>
                    {Number(d.investissementTotal).toLocaleString("fr-FR")} €
                  </td>
                  <td>
                    <span
                      className={`${styles.badge} ${
                        styles["badge" + d.statut.valeur] || ""
                      }`}
                    >
                      {d.statut.libelle}
                    </span>
                  </td>
                  <td>
                    {d.statutModifieLe
                      ? tools.formatDate(d.statutModifieLe)
                      : "—"}
                  </td>
                  <td>
                    {d.todosProgression && (
                      <TodosProgressionBadge progression={d.todosProgression} />
                    )}
                  </td>
                  <td>
                    <span
                      className={`${styles.badge} ${
                        d.echeancierRenseigne
                          ? styles.badgeEcheancierOk
                          : styles.badgeEcheancierKo
                      }`}
                      title="Indicateur informatif — n'empêche aucune action"
                    >
                      {d.echeancierRenseigne ? "OK" : "Incomplet"}
                    </span>
                  </td>
                  <td>
                    {/* LS-745 — vrai lien : le Ctrl/Cmd+clic de Yanis ouvre
                        le dossier dans un nouvel onglet. */}
                    <Link
                      className={styles.btnOpen}
                      to={"/financements/" + d.uuid}
                    >
                      Ouvrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <CustomPagination
            total={meta.total}
            activePage={meta.current_page}
            handlePageParamsChange={({ page }) => setPage(page)}
          />
        </>
      )}
    </div>
  );
};

export default withRouter(FinanceListV2);
